import { getApiClient } from './authApiClient';
import { parseDnbIdentityResponse, normalizeUSState } from '../features/dnbIdentitySearch';

const validTenant = tenantId => {
  if (typeof tenantId !== 'string' || !/^[A-Za-z0-9_-]{1,100}$/.test(tenantId)) throw new Error('A valid account is required.');
  return tenantId;
};

// Reuses the existing authenticated clients. No token handling or entitlement writes.
export async function checkDnbIdentitySearchAccess(tenantId, { signal } = {}) {
  const tenant = validTenant(tenantId);
  const response = await getApiClient('tenant').get(`/tenants/fetch/${encodeURIComponent(tenant)}`, { signal, timeout: 15000, _retry: true });
  const envelope = response.data;
  const record = envelope?.data;
  const identifiers = [record?.id, record?.tenantId].filter(value => value !== undefined && value !== null);
  if (envelope?.success === false || !record || !identifiers.length || identifiers.some(value => value !== tenant)) throw new Error('Account access could not be verified.');
  if (record.isDNBSearchEnabled !== true) throw Object.assign(new Error('D&B company search is not enabled for this account.'), { code: 'DNB_SEARCH_DISABLED' });
  return { tenantId: tenant, enabled: true };
}

export async function searchDnbIdentity({ tenantId, name, state }, { signal } = {}) {
  const tenant = validTenant(tenantId);
  const legalName = typeof name === 'string' ? name.trim() : '';
  const stateCode = normalizeUSState(state);
  if (legalName.length < 2 || legalName.length > 256 || [...legalName].some(character => character.charCodeAt(0) < 32 || character.charCodeAt(0) === 127)) throw new Error('Enter a legal name between 2 and 256 characters.');
  if (!stateCode) throw new Error('Choose a US state.');
  await checkDnbIdentitySearchAccess(tenant, { signal });
  if (signal?.aborted) throw new Error('Identity search was cancelled.');
  // The endpoint accepts country/name only. State stays local; never append it
  // to the legal name or silently use a different provider on empty/error results.
  const response = await getApiClient('company').get('/dnb/companies/search', {
    params: { name: legalName, tenantId: tenant, iso2Code: 'US' },
    signal, timeout: 30000, _retry: true,
  });
  return parseDnbIdentityResponse(response.data, stateCode);
}
